import type {OnDragEndResponder} from '@hello-pangea/dnd';
import {DragDropContext, Draggable, Droppable} from '@hello-pangea/dnd';
import {
  Box,
  Button,
  ColorSwatch,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import {IconGripVertical, IconPlus, IconX} from '@tabler/icons-react';
import React from 'react';
import {ActionIcon} from 'src/design/atoms/ActionIcon';

export interface OptionListEditorItem {
  id: string;
  value: string;
  color?: string;
}

export interface OptionListEditorColorOption {
  value: string;
  label: string;
  swatch: string;
}

/**
 * Add/edit/remove row list for a set of free-text options — one `TextInput`
 * + delete icon per item, plus an "add" button underneath. When `onReorder`
 * is supplied, rows also get a drag handle for reordering.
 * @registry Generic option/unit list editor (dropdown options, measurement
 * units, etc) — pure add/edit/remove/reorder rows, no domain knowledge.
 *
 * Domain-free: consumers own the underlying data shape (option label vs.
 * measurement unit) and translate to/from `{id, value}` items themselves.
 * `onReorder` reports source/destination indices only — consumers own
 * reordering their own array and any ordering metadata (e.g. `orderId`).
 */
export interface OptionListEditorProps {
  items: OptionListEditorItem[];
  onAdd: () => void;
  onChange: (id: string, value: string) => void;
  onRemove: (id: string) => void;
  onReorder?: (sourceIndex: number, destinationIndex: number) => void;
  addLabel?: string;
  valuePlaceholder?: string;
  disabled?: boolean;
  addDisabled?: boolean;
  itemError?: (id: string) => boolean;
  emptyMessage?: string;
  getItemLabel?: (index: number) => string;
  /**
   * When supplied alongside `onColorChange`, each row gets a compact color
   * picker between its text input and delete icon. Consumer owns the
   * available palette — this molecule holds no domain colors.
   */
  colorOptions?: OptionListEditorColorOption[];
  onColorChange?: (id: string, color: string | null) => void;
}

export const OptionListEditor: React.FC<OptionListEditorProps> = ({
  items,
  onAdd,
  onChange,
  onRemove,
  onReorder,
  addLabel = '+ Add Option',
  valuePlaceholder,
  disabled = false,
  addDisabled = false,
  itemError,
  emptyMessage,
  getItemLabel = index => `Option ${index + 1}`,
  colorOptions,
  onColorChange,
}) => {
  const handleDragEnd: OnDragEndResponder = result => {
    if (!result.destination) return;
    onReorder?.(result.source.index, result.destination.index);
  };

  const renderRow = (
    item: OptionListEditorItem,
    index: number,
    dragHandle?: React.ReactNode
  ) => (
    <Group align="center" wrap="nowrap">
      {dragHandle}
      <TextInput
        flex={1}
        aria-label={getItemLabel(index)}
        placeholder={valuePlaceholder}
        value={item.value}
        disabled={disabled}
        error={itemError?.(item.id)}
        onChange={e => onChange(item.id, e.currentTarget.value)}
      />
      {colorOptions && onColorChange && (
        <Select
          w={110}
          aria-label={`${getItemLabel(index)} color`}
          placeholder="None"
          disabled={disabled}
          clearable
          value={item.color ?? null}
          onChange={value => onColorChange(item.id, value)}
          data={colorOptions.map(option => ({
            value: option.value,
            label: option.label,
          }))}
          renderOption={({option}) => {
            const colorOption = colorOptions.find(
              c => c.value === option.value
            );
            return (
              <Group gap="xs" wrap="nowrap">
                {colorOption && (
                  <ColorSwatch color={colorOption.swatch} size={14} />
                )}
                <Text size="sm">{option.label}</Text>
              </Group>
            );
          }}
          leftSection={
            item.color ? (
              <ColorSwatch
                color={
                  colorOptions.find(c => c.value === item.color)?.swatch ??
                  'transparent'
                }
                size={14}
              />
            ) : undefined
          }
        />
      )}
      <ActionIcon
        onClick={() => onRemove(item.id)}
        color="red"
        tooltipText="Delete option"
        disabled={disabled}>
        <IconX size={16} />
      </ActionIcon>
    </Group>
  );

  return (
    <Stack gap="xs">
      {onReorder && !disabled ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="option-list-editor">
            {provided => (
              <Stack
                gap="xs"
                ref={provided.innerRef}
                {...provided.droppableProps}>
                {items.map((item, index) => (
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {dragProvided => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}>
                        {renderRow(
                          item,
                          index,
                          <Box
                            {...dragProvided.dragHandleProps}
                            aria-label={`Reorder ${getItemLabel(index)}`}
                            c="dimmed"
                            style={{flexShrink: 0, cursor: 'grab'}}>
                            <IconGripVertical size={16} />
                          </Box>
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        items.map((item, index) => (
          <React.Fragment key={item.id}>
            {renderRow(item, index)}
          </React.Fragment>
        ))
      )}

      {items.length === 0 && emptyMessage && (
        <Text size="xs" c="red">
          {emptyMessage}
        </Text>
      )}

      <Button
        color="blue"
        size="sm"
        variant="subtle"
        leftSection={<IconPlus size={16} />}
        disabled={disabled || addDisabled}
        onClick={onAdd}>
        {addLabel}
      </Button>
    </Stack>
  );
};
